'use client';

import React from 'react';
import { Heart, Calendar, Sparkles } from 'lucide-react';
import type { EventConfig, ColorTheme } from './EventSetupClient';

interface PreviewCardProps {
  config: EventConfig;
}

const themeColors: Record<ColorTheme, { boy: string; girl: string; bg: string; badge: string }> = {
  classic: {
    boy: 'bg-boy-gradient',
    girl: 'bg-girl-gradient',
    bg: 'from-blue-50 via-white to-pink-50',
    badge: 'bg-gradient-to-r from-blue-100 to-pink-100',
  },
  pastel: {
    boy: 'bg-gradient-to-br from-sky-200 to-blue-200',
    girl: 'bg-gradient-to-br from-rose-200 to-pink-200',
    bg: 'from-sky-50 via-white to-rose-50',
    badge: 'bg-gradient-to-r from-sky-100 to-rose-100',
  },
  bold: {
    boy: 'bg-gradient-to-br from-blue-700 to-blue-900',
    girl: 'bg-gradient-to-br from-pink-700 to-rose-900',
    bg: 'from-blue-100 via-white to-pink-100',
    badge: 'bg-gradient-to-r from-blue-200 to-pink-200',
  },
  sunset: {
    boy: 'bg-gradient-to-br from-orange-400 to-amber-500',
    girl: 'bg-gradient-to-br from-rose-400 to-pink-500',
    bg: 'from-orange-50 via-white to-rose-50',
    badge: 'bg-gradient-to-r from-orange-100 to-rose-100',
  },
  mint: {
    boy: 'bg-gradient-to-br from-teal-400 to-cyan-500',
    girl: 'bg-gradient-to-br from-purple-400 to-violet-500',
    bg: 'from-teal-50 via-white to-purple-50',
    badge: 'bg-gradient-to-r from-teal-100 to-purple-100',
  },
  'olive-lilac': {
    boy: 'bg-gradient-to-br from-[#6B7C3E] to-[#4E5C2A]',
    girl: 'bg-gradient-to-br from-[#B07FD4] to-[#8A5CB8]',
    bg: 'from-[#f0f2e8] via-white to-[#f5eefb]',
    badge: 'bg-gradient-to-r from-[#e4e9d0] to-[#ecdff7]',
  },
  'golden-silver': {
    boy: 'bg-gradient-to-br from-[#C9A84C] to-[#A07830]',
    girl: 'bg-gradient-to-br from-[#9BA8B5] to-[#6E7E8C]',
    bg: 'from-[#fdf8ec] via-white to-[#f0f3f6]',
    badge: 'bg-gradient-to-r from-[#f5e9c0] to-[#dde4ea]',
  },
};

export default function PreviewCard({ config }: PreviewCardProps) {
  const theme = themeColors[config.colorTheme] || themeColors.classic;
  const title = config.eventTitle || 'Revelación de Género';
  const p1 = config.parentName1 || 'Mamá';
  const p2 = config.parentName2 || 'Papá';
  const message = config.customMessage || '¡Ayúdanos a descubrir si es niño o niña!';

  return (
    <div className={`rounded-2xl overflow-hidden bg-gradient-to-br ${theme.bg} border border-border`}>
      {/* Header strip */}
      <div className="px-5 pt-5 pb-4 text-center">
        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-600 text-foreground mb-3 ${theme.badge}`}>
          <Sparkles size={10} />
          BabyReveal
        </div>
        <h3 className="text-lg font-800 text-foreground mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground font-500">
          {p1} & {p2} te invitan
        </p>
        {config.dueDate && (
          <div className="inline-flex items-center gap-1 mt-2 text-xs text-muted-foreground">
            <Calendar size={10} />
            Fecha probable: {config.dueDate}
          </div>
        )}
        {config.babyNameHint && (
          <div className="mt-2 text-xs text-muted-foreground italic">
            Pista: {config.babyNameHint}
          </div>
        )}
      </div>

      {/* Message */}
      <div className="px-5 pb-4">
        <p className="text-sm text-center text-foreground/80 font-500 leading-relaxed">{message}</p>
      </div>

      {/* Choice buttons preview */}
      <div className="px-5 pb-5 grid grid-cols-2 gap-3">
        <div className={`${theme.boy} rounded-xl p-3 text-center text-white`}>
          <div className="text-2xl mb-1">👶💙</div>
          <div className="text-sm font-700">Niño</div>
        </div>
        <div className={`${theme.girl} rounded-xl p-3 text-center text-white`}>
          <div className="text-2xl mb-1">👶🩷</div>
          <div className="text-sm font-700">Niña</div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 pb-4 flex items-center justify-center gap-1 text-xs text-muted-foreground">
        <Heart size={10} className="text-primary" />
        Hecho con BabyReveal
      </div>
    </div>
  );
}